/* eslint-disable no-useless-escape */
import * as React from 'react';
import {
	Modal,
	Button,
	Grid,
	XFilledIcon
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractRulesForm';
import { RuleAction } from '../../../../models/enums/DNDR/RuleAction';
import { RuleRegion } from '../../../../models/enums/DNDR/RuleRegion';
import { TrafficDirection } from '../../../../models/enums/DNDR/TrafficDirection';
import { VirtualNetwork } from '../../../../models/entities/AzureResources/VirtualNetwork';
import SelectField from '../../../common/form-controls/SelectField';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import { RuleOperation } from '../../../../models/enums/DNDR/RuleOperation';
import { Address4, Address6 } from 'ip-address';
import { DoNotDecryptRequestsDirection } from '../../../../models/enums/DNDR/DoNotDecryptRequestsDirection';

interface RuleModalSubToOnPremProps extends RulesFormProps {
	subscriptionRequestName: string;
}




interface SubToOnPremRuleStateForm extends IRuleStateForm {
	name: string;
	trafficDirection: TrafficDirection;
	protocol: string;
	destinationURLs: string;
}
interface RuleModalSubToOnPremState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalSubToOnPrem extends AbstractRulesForm<
	RuleModalSubToOnPremProps,
	RuleModalSubToOnPremState
> {

	constructor(props: RuleModalSubToOnPremProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};

		this.initFormModel();
	}
	
	isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules:
					[
						{
							assert: () => this.state.stateForm.operation !== RuleOperation.Add
								|| this.state.stateForm.name?.length <= 24,
							message: 'Name cannot be longer than 24 characters.',
						},
						{
							assert: () => !this.state.stateForm.name?.includes(' '),
							message: 'Name cannot contain spaces.',
						},
						{
							assert: () => this.state.stateForm.operation !== RuleOperation.Add
								|| /^(?![-_])[a-zA-Z][a-zA-Z-_]*(?<![-_])$/.test(this.state.stateForm.name ?? ''),
							message: 'Only letters (uppercase and lowercase), dashes, underscores are allowed. No trailing or beginning dashes or underscores.',
						}
					]
			},
		});

		this.formModel.addField('trafficDirection', {
			getValue: () => this.state.stateForm.trafficDirection,
			validation: {
				required: true,
			},
		});

		this.formModel.addField('destinationURLs', {
			getValue: () => this.parseMultipleValues(this.state.stateForm.destinationURLs),
			validation: {
				required: false,
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.destinationURLs),
						message: 'Empty values are not allowed.'
					},
					{
						assert: () => !(this.state.stateForm.destinationURLs.includes('https://')
							|| this.state.stateForm.destinationURLs.includes('http://')),
						message: 'URL cannot have "http" or "https"'
					},
					{
						assert: (value) => {
							const urls = this.parseMultipleValues(value)?.split(';');
							return urls?.every(url => this.isURLValid(url));
						},
						message: (value) => {
							const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
							return `${invalidAddress} is an invalid URL.`;
						}
					}
				]
			}
		});

		// Override sourceIPs/destinationIPs/destinationPorts with traffic-direction-aware required
		this.formModel.fields.sourceIPs.validation.required =
			() => this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;

		this.formModel.fields.sourceIPs.validation.rules = [];

		this.formModel.fields.destinationIPs.validation.required =
			() => this.state.stateForm.trafficDirection?.name === TrafficDirection.In.name
				|| this.state.stateForm.trafficDirection == null;

		this.formModel.fields.destinationIPs.validation.rules = [];

		this.formModel.fields.destinationPorts.validation.required =
			() => !!(this.state.stateForm.sourceIPs || this.state.stateForm.destinationIPs);
	}

	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			name: '',
			destinationURLs: '',
			trafficDirection: null,
		};
	}

	getRegionOptions(): RuleRegion[] {
		var subToOnPremRegions = new RuleRegion().listByDirection(DoNotDecryptRequestsDirection.SubscriptionToOnPrem, this.props.validVirtualNetworks);
		const wwSubscriptionsNames = ['APPS_SharedSvcs_WW_DEV', 'APPS_SharedSvcs_WW_LAB', 'APPS_SharedSvcs_WW_PRD'];
		return wwSubscriptionsNames.includes(this.props.subscriptionRequestName) ? subToOnPremRegions : subToOnPremRegions.filter(region => region !== RuleRegion.WW);
	}

	getFilteredMappings() {
		if (!this.props.completedPORRules?.hasCompletedPORRules || !this.props.completedPORRules?.ruleMappings) {
			return [];
		}

		const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;
		const dirFilter = isOutbound ? 'Out' : 'In';

		return this.props.completedPORRules.ruleMappings
			.filter(m => m.trafficDirection === dirFilter);
	}

	getStateFormHandlers() {
		const handlers = super.getStateFormHandlers();

		handlers.sourceIPs = {
			...handlers.sourceIPs,
			onChange: (event) => {
				this.handleStateFormChange('sourceIPs', event.target.value);
				this.handleStateFormChange('destinationPorts', '');
				if (this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name) {
					this.handleStateFormChange('destinationIPs', '');
				}
			}
		};

		handlers.destinationIPs = {
			...handlers.destinationIPs,
			onChange: (event) => {
				this.handleStateFormChange('destinationIPs', event.target.value);
				this.handleStateFormChange('destinationPorts', '');
				if (this.state.stateForm.trafficDirection?.name !== TrafficDirection.Out.name) {
					this.handleStateFormChange('sourceIPs', '');
				}
			}
		};

		return handlers;
	}

	getSourceIPOptions() {
		if (this.props.completedPORRules?.hasCompletedPORRules) {
			const mappings = this.getFilteredMappings();
			const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;

			if (isOutbound) {
				// Outbound: source IPs are the first selection
				const ips = [...new Set(mappings.map(m => m.sourceIP))];
				return ips.map(ip => ({ value: ip, text: ip }));
			} else {
				// Inbound: source IPs depend on selected destination IP
				const selectedDestIP = this.state.stateForm.destinationIPs?.trim();
				if (!selectedDestIP) return [];

				const ips = [...new Set(
					mappings
						.filter(m => m.destinationIP === selectedDestIP)
						.map(m => m.sourceIP)
				)];
				return ips.map(ip => ({ value: ip, text: ip }));
			}
		}

		// Fallback to vNet subnets
		const vNetDictionary = {
			'America': 'America', 'EAME': 'EAME', 'AP': 'AP',
			'USSC': 'southcentralus', 'USE1': 'eastus',
			'ASST': 'southeastasia', 'ASEA': 'eastasia', 'CNN3': 'chinanorth3', 'WW': 'WW',
		};
		let vnetsByRegion: VirtualNetwork[] = this.props.validVirtualNetworks.filter(
			vnet => vNetDictionary[this.state.stateForm.region?.name]?.toLowerCase() == vnet.location.toLowerCase()
		);
		const subnets = vnetsByRegion.flatMap(vNet => vNet.properties.subnets);
		const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix).filter(a => a != null);
		return addressSpaces.map(ip => ({ value: ip, text: ip }));
	}

	getDestinationIPOptions() {
		if (this.props.completedPORRules?.hasCompletedPORRules) {
			const mappings = this.getFilteredMappings();
			const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;

			if (isOutbound) {
				// Outbound: destination IPs depend on selected source IP
				const selectedSrcIP = this.state.stateForm.sourceIPs?.trim();
				if (!selectedSrcIP) return [];

				const ips = [...new Set(
					mappings
						.filter(m => m.sourceIP === selectedSrcIP)
						.map(m => m.destinationIP)
				)];
				return ips.map(ip => ({ value: ip, text: ip }));
			} else {
				// Inbound: destination IPs are the first selection
				const ips = [...new Set(mappings.map(m => m.destinationIP))];
				return ips.map(ip => ({ value: ip, text: ip }));
			}
		}
		return this.getSourceIPOptions();
	}

	getDestinationPortOptions() {
		if (this.props.completedPORRules?.hasCompletedPORRules) {
			const mappings = this.getFilteredMappings();
			const selectedSrcIP = this.state.stateForm.sourceIPs?.trim();
			const selectedDstIP = this.state.stateForm.destinationIPs?.trim();

			if (!selectedSrcIP || !selectedDstIP) return [];

			const ports = [...new Set(
				mappings
					.filter(m => m.sourceIP === selectedSrcIP && m.destinationIP === selectedDstIP)
					.map(m => m.destinationPort)
			)];
			return ports.map(port => ({ value: port, text: port }));
		}
		return [];
	}

	resetIPsValidations() {
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	cleanIPs() {
		this.handleStateFormChange('sourceIPs', '')
		this.handleStateFormChange('destinationIPs', '')
		this.handleStateFormChange('destinationPorts', '')
	}

	loadIPsValidations(direction) {
		this.resetIPsValidations();

		// When using completed POR rules, IPs come from dropdowns � no format validation needed
		if (this.props.completedPORRules?.hasCompletedPORRules) {
			return;
		}

		if (direction == 1) {
			this.formModel.fields.sourceIPs.validation.rules.push(
				this.IpInputValidationRule({ onlyPrivateAddresses: true }),
				this.IpIsInAddressSpacesRule()
			);
			this.formModel.fields.destinationIPs.validation.rules.push(
				this.IpInputValidationRule({ onlyPrivateAddresses: true })
			);
		} else {
			this.formModel.fields.sourceIPs.validation.rules.push(
				this.IpInputValidationRule({ onlyPrivateAddresses: true }),
			);
			this.formModel.fields.destinationIPs.validation.rules.push(
				this.IpInputValidationRule({ onlyPrivateAddresses: true }),
				this.IpIsInAddressSpacesRule()
			);
		}
	}

	handleTrafficDirection(event) {
		this.handleStateFormChange('trafficDirection', new TrafficDirection().fromValue(event.target.value));
		//0 = In ; 1 == Out

		this.cleanIPs();
		this.loadIPsValidations(this.state.stateForm.trafficDirection?.value)
		
	}

	render() {
		return (
			<Modal show={this.props.visible} onHide={() => this.props.onClose}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>
							{this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size='small' />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						{ !this.isModalForNewRule() &&
							<ToggleField
								{...this.stateFormHandler().operation}
								label="Operation"
								disabled={this.props.isReadOnly}
							/>
						}
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={
										(event) => {
											this.handleStateFormChange('name', event.target.value);
										}
									}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									{...this.stateFormHandler().region}
									options={this.getRegionOptions()}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>
								<SelectField
									label="Traffic Direction"
									value={this.state.stateForm.trafficDirection?.value}
									options={[
										{ text: 'Inbound (XOMNetwork -> Subscription)', value: TrafficDirection.In.value },
										{ text: 'Outbound (Subscription -> XOMNetwork)', value: TrafficDirection.Out.value },
									]}
									className="full-width"
									onChange={(event) => this.handleTrafficDirection(event)}
									validation={this.formModel.fields.trafficDirection.validation}
									disabled={!this.formCanBeEdited()}
								/>
							</Grid.Item>
							<Grid.Item>
							</Grid.Item>
						</Grid>
						{this.state.stateForm.trafficDirection?.name == TrafficDirection.Out.name &&
							<>
							{this.formCanBeEdited() ? 
								<Grid variant="2-up">
									<Grid.Item>
										<SelectField
											{...this.stateFormHandler().sourceIPs}
											options={this.getSourceIPOptions()}
											label="Source (IP)"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									<Grid.Item>
										<SelectField
											{...this.stateFormHandler().destinationIPs}
											options={this.getDestinationIPOptions()}
											label="Destination (IP)"
											disabled={!this.formCanBeEdited() || !this.state.stateForm.sourceIPs}
										/>
									</Grid.Item>
								</Grid>
								:
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											label="Source (IP)"
											value={this.stateFormHandler().sourceIPs.value}
											disabled={true}
										/>
									</Grid.Item>
									<Grid.Item>
										<TextAreaField
											label="Destination (IP)"
											value={this.stateFormHandler().destinationIPs.value}
											disabled={true}
										/>
									</Grid.Item>
								</Grid>
							}
							</>
						}
						{(this.state.stateForm.trafficDirection == null || this.state.stateForm.trafficDirection?.name == TrafficDirection.In.name) &&
							<>
							{this.formCanBeEdited() ?
								<Grid variant="2-up">
									<Grid.Item>
										<SelectField
											{...this.stateFormHandler().sourceIPs}
											options={this.getSourceIPOptions()}
											label="Source (IP)"
											disabled={!this.formCanBeEdited() || !this.state.stateForm.destinationIPs}
										/>
									</Grid.Item>
									<Grid.Item>
										<SelectField
											{...this.stateFormHandler().destinationIPs}
											options={this.getDestinationIPOptions()}
											label="Destination (IP)"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
								</Grid>
								:
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											label="Source (IP)"
											value={this.stateFormHandler().sourceIPs.value}
											disabled={true}
										/>
									</Grid.Item>
									<Grid.Item>
										<TextAreaField
											label="Destination (IP)"
											value={this.stateFormHandler().destinationIPs.value}
											disabled={true}
										/>
									</Grid.Item>
								</Grid>
							}
							</>
						}
						<Grid variant="2-up">
							<Grid.Item>
								{this.formCanBeEdited() ? 
									<SelectField
										{...this.stateFormHandler().destinationPorts}
										options={this.getDestinationPortOptions()}
										label="Destination Port"
										disabled={!this.formCanBeEdited() || this.getDestinationPortOptions().length === 0}
									/>
									:
									<TextAreaField
										label="Destination Port"
										value={this.stateFormHandler().destinationPorts.value}
										disabled={true}
									/>
								}
							</Grid.Item>
							<Grid.Item />
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>
								<TextAreaField
									{...this.stateFormHandler().description}
									label="Description"
									disabled={!this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
						{/* <Grid variant="2-up">
							<Grid.Item>
								<SelectField
									label="Protocol"
									value={this.state.stateForm.protocol}
									options={['Any', 'TCP', 'UDP', 'ICMP']}
									onChange={(event) => this.handleStateFormChange('protocol', event.target.value)}
									validation={this.formModel.fields.protocol.validation}
									disabled={!this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid> */}
					</Modal.Window.Body>
					{ !this.props.isReadOnly &&
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={!this.formModel.isValid() && this.state.stateForm.operation.name !== RuleOperation.Remove.name}						
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button
										variant="secondary"
										onClick={this.handleCancel}
									>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					}

				</Modal.Window>
			</Modal>
		);
	}
}